import { Controller } from "@hotwired/stimulus"
import { CIHotspotEditor } from "@cloudimage/hotspot/editor"

export default class extends Controller {
  static targets = ["mount", "hotspotsInput", "placeholder", "fileInput"]
  static values = {
    side: String,
    imageUrl: String,
    initialHotspots: { type: String, default: "[]" }
  }

  connect() {
    this.form = this.element.closest("form")
    this.editor = null
    this.objectUrl = null

    if (this.form) {
      this.blockEditorSubmitHandler = (event) => this.blockEditorSubmit(event)
      this.form.addEventListener("submit", this.blockEditorSubmitHandler, true)

      this.syncSubmitHandler = () => this.syncBeforeSubmit()
      this.form.addEventListener("submit", this.syncSubmitHandler, true)
    }

    if (this.hasImageUrlValue && this.imageUrlValue) {
      this.initEditor(this.imageUrlValue)
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("submit", this.syncSubmitHandler, true)
      this.form.removeEventListener("submit", this.blockEditorSubmitHandler, true)
    }
    this.destroyEditor()
    this.revokeObjectUrl()
    this.domObserver?.disconnect()
    this.domObserver = null
    this.mountTarget?.removeEventListener("click", this.handleMountClick, true)
  }

  previewFile(event) {
    const file = event.target.files?.[0]
    if (!file) return

    this.revokeObjectUrl()
    this.objectUrl = URL.createObjectURL(file)
    this.initEditor(this.objectUrl)
  }

  initEditor(src) {
    if (!this.hasMountTarget) return

    this.destroyEditor()
    this.mountTarget.classList.remove("hidden")
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")

    const hotspots = this.parseHotspots(this.initialHotspotsValue)

    this.editor = new CIHotspotEditor(this.mountTarget, {
      src,
      hotspots,
      defaultTrigger: "click",
      onChange: (items) => this.writeHotspots(items)
    })

    this.writeHotspots(hotspots)
    this.isolateEditorFromForm()
  }

  isolateEditorFromForm() {
    const fix = () => {
      this.mountTarget.querySelectorAll("button").forEach((button) => {
        button.type = "button"
      })

      this.mountTarget.querySelectorAll('input[type="url"]').forEach((input) => {
        input.removeAttribute("required")
        input.type = "text"
      })

      this.removeUnwantedHotspotFields()
    }

    fix()
    this.domObserver?.disconnect()
    this.domObserver = new MutationObserver(fix)
    this.domObserver.observe(this.mountTarget, { childList: true, subtree: true })

    this.mountTarget.addEventListener("click", this.handleMountClick, true)
  }

  removeUnwantedHotspotFields() {
    const blockedLabels = [
      "price",
      "original price",
      "image url",
      "link url",
      "cta text"
    ]

    this.mountTarget.querySelectorAll("label").forEach((label) => {
      const labelText = label.textContent?.trim().toLowerCase()
      if (!labelText) return
      if (!blockedLabels.includes(labelText)) return

      const fieldWrap = label.closest(".ci-editor-form-group, .ci-editor-field, .ci-editor-row")
      if (fieldWrap) {
        fieldWrap.remove()
      } else {
        label.remove()
      }
    })
  }

  handleMountClick = (event) => {
    const button = event.target.closest("button")
    if (!button || !this.mountTarget.contains(button)) return

    button.type = "button"
  }

  blockEditorSubmit(event) {
    const submitter = event.submitter
    if (!submitter || !this.mountTarget.contains(submitter)) return

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  syncBeforeSubmit() {
    if (this.editor) {
      this.writeHotspots(this.editor.getHotspots())
    }
  }

  writeHotspots(items) {
    if (!this.hasHotspotsInputTarget) return
    this.hotspotsInputTarget.value = JSON.stringify(items || [])
  }

  destroyEditor() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
    if (this.hasMountTarget) this.mountTarget.innerHTML = ""
  }

  revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  parseHotspots(raw) {
    if (!raw) return []
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
}
